/**
 * Same idea as apps/guest-portal/src/lib/geofence.ts (no shared package for
 * it yet, so this is a small, deliberate duplicate) — but for staff shifts
 * instead of guest ordering: useStaffSession polls this to auto clock-out
 * a signed-in staff member whose device has drifted away from the store,
 * as a safety net for a forgotten manual clock-out. A wider default than
 * guest ordering's 300m on purpose — a false-positive auto clock-out (staff
 * mid-shift, still on site, wrongly ended) is more disruptive than a guest
 * being asked to move a bit closer to order. Overridden by
 * core.stores.geofence_radius_m when a store sets one (e.g. two
 * neighboring businesses close enough that this default would cover both).
 */
export const STAFF_GEOFENCE_RADIUS_M = 500;

export type GeofenceReason = "ok" | "too_far" | "denied" | "unavailable" | "timeout" | "no_store_location";

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two lat/lng points, in meters. */
export function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const EARTH_RADIUS_M = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface StaffGeofenceCheck {
  /** true = confirmed out of range; false = in range; null = couldn't
   * confirm either way (no store location set) — never treated as "out of
   * range" on its own. Kept for the periodic auto clock-out poller, which
   * deliberately never ends a shift over a permission technicality — see
   * reason below for the stricter, blocking checks (new clock-ins). */
  outOfRange: boolean | null;
  distanceM?: number;
  reason: GeofenceReason;
}

/** Never rejects. `outOfRange` stays conservative (null unless confirmed
 * too far) for the auto clock-out poller's sake, but `reason` reports
 * denied/unavailable/timeout distinctly so a NEW clock-in (see
 * useStaffSession's geofenceBlockError) can choose to block on those too —
 * "can't tell" is fine to shrug off for an already-working shift, but not
 * for letting someone clock in at all. */
export function checkStaffGeofence(store: { latitude: string | null; longitude: string | null; geofence_radius_m?: number | null }): Promise<StaffGeofenceCheck> {
  if (store.latitude == null || store.longitude == null) return Promise.resolve({ outOfRange: null, reason: "no_store_location" });
  if (!("geolocation" in navigator)) return Promise.resolve({ outOfRange: null, reason: "unavailable" });

  const storeLat = Number(store.latitude);
  const storeLon = Number(store.longitude);
  const radiusM = store.geofence_radius_m ?? STAFF_GEOFENCE_RADIUS_M;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const distanceM = haversineMeters(storeLat, storeLon, pos.coords.latitude, pos.coords.longitude);
        const tooFar = distanceM > radiusM;
        resolve({ outOfRange: tooFar, distanceM, reason: tooFar ? "too_far" : "ok" });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve({ outOfRange: null, reason: "denied" });
        else if (err.code === err.TIMEOUT) resolve({ outOfRange: null, reason: "timeout" });
        else resolve({ outOfRange: null, reason: "unavailable" });
      },
      // enableHighAccuracy: false — WiFi/cell-based positioning resolves in
      // a fraction of a second to ~2s typically, versus GPS's multi-second-
      // to-timeout behavior indoors (exactly where a till sits). A 500m+
      // geofence radius was never meant to be meter-precise, so the speed
      // trade-off costs nothing real. This gate blocks the entire staff
      // picker/PIN screen on every reload — see useStoreLocationGate.ts —
      // so the common case needs to be fast; `timeout` stays as a safety
      // net for a genuinely bad case, not the thing being optimized here.
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 },
    );
  });
}
